/**
 * HexBoard.tsx — React+SVG hex board renderer (WEB.4.UI.1)
 *
 * 3-layer composite:
 *   Layer 0: <video>  bg normal loop-1.mp4 (animated ocean)
 *   Layer 1: <img>    IslandMap.png (pixel-art island)
 *   Layer 2: <svg>    33 flat-top hex overlays + player tokens
 *
 * Visual source-of-truth: design-mockups/web.4.ui.1-hexboard.html (approved)
 * Position source-of-truth: data/board/tile-positions.json (frozen, do not recompute)
 */

import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { bgmKeyForEraRound, crossfadeBGM } from "../../audio/audio.js";
import { SEAT_COLORS, type SeatIndex, useGameStore } from "../../store/game-store.js";
import styles from "./HexBoard.module.css";
import { CANVAS_H, CANVAS_W, HEX_R, flatTopHexPoints, tileCenter } from "./hex-geometry.js";
import { TILE_COUNT, TILE_POSITIONS } from "./tile-positions.js";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TOKEN_R = 8;

/** Stable index array [0..32] for .map() keys */
const TILE_INDICES: readonly number[] = Array.from({ length: TILE_COUNT }, (_, i) => i);

/** 4-slot 2×2 offsets when ≥2 tokens share a tile */
const TOKEN_OFFSETS = [
  { dx: -6, dy: -5 },
  { dx: 6, dy: -5 },
  { dx: -6, dy: 5 },
  { dx: 6, dy: 5 },
] as const;

// ---------------------------------------------------------------------------
// Glyph mapping — ONLY special-function tiles; terrain = no glyph
// ---------------------------------------------------------------------------

interface GlyphDef {
  glyph: string;
  fill: string;
}

const TILE_GLYPH: Partial<Record<string, GlyphDef>> = {
  Start: { glyph: "★", fill: "#FFB420" },
  Event: { glyph: "?", fill: "#FFBB66" },
  Jail: { glyph: "⊠", fill: "#EF5350" },
  Policy: { glyph: "⚑", fill: "#88FFB0" },
  Trading: { glyph: "¤", fill: "#FFB420" },
  Teleport: { glyph: "⊕", fill: "#B8A8FF" },
  // Terrain types — no glyph. Listed for exhaustiveness documentation:
  // Urban, Plains, Desert, Coast, River, Tundra, Mountain, Forest, Volcanic
};

// ---------------------------------------------------------------------------
// Hex fill/stroke helpers
// ---------------------------------------------------------------------------

function seatColorRgb(seat: number): string {
  const hex = SEAT_COLORS[seat] ?? SEAT_COLORS[0];
  const r = Number.parseInt(hex.slice(1, 3), 16);
  const g = Number.parseInt(hex.slice(3, 5), 16);
  const b = Number.parseInt(hex.slice(5, 7), 16);
  return `${r},${g},${b}`;
}

interface HexVisual {
  fill: string;
  stroke: string;
  strokeWidth: number;
  isActive: boolean;
}

function hexVisual(
  index: number,
  hoveredTile: number | null,
  activeTile: number | null,
  ownerSeat: SeatIndex | undefined,
): HexVisual {
  const isActive = activeTile === index;
  const isHovered = hoveredTile === index;

  if (ownerSeat !== undefined) {
    const rgb = seatColorRgb(ownerSeat);
    return {
      fill: `rgba(${rgb},0.45)`,
      stroke: `rgba(${rgb},0.75)`,
      strokeWidth: 1.5,
      isActive,
    };
  }
  if (isActive) {
    return { fill: "rgba(255,180,32,0.30)", stroke: "#FFB420", strokeWidth: 2.5, isActive: true };
  }
  if (isHovered) {
    return { fill: "rgba(255,180,32,0.25)", stroke: "#FFB420", strokeWidth: 2.5, isActive: false };
  }
  return {
    fill: "rgba(255,255,255,0.18)",
    stroke: "rgba(255,255,255,0.45)",
    strokeWidth: 1.5,
    isActive: false,
  };
}

// ---------------------------------------------------------------------------
// HexTile sub-component
// ---------------------------------------------------------------------------

interface HexTileProps {
  index: number;
  hoveredTile: number | null;
  activeTile: number | null;
  ownerSeat: SeatIndex | undefined;
  onHoverEnter: (i: number, e: React.MouseEvent) => void;
  onHoverLeave: () => void;
  onClick: (i: number) => void;
  // WEB.4.UI.7 picker props
  isHighlighted?: boolean | undefined;
  isPickerSelected?: boolean | undefined;
  pickerMode?: PickerMode | undefined;
  /** facilityId built on this tile (e.g. "coal", "solar"), or undefined */
  facilityId?: string | undefined;
}

function HexTile({
  index,
  hoveredTile,
  activeTile,
  ownerSeat,
  onHoverEnter,
  onHoverLeave,
  onClick,
  isHighlighted,
  isPickerSelected,
  pickerMode,
  facilityId,
}: HexTileProps): React.ReactElement {
  const { x, y } = tileCenter(index);
  const pos = TILE_POSITIONS[index];
  if (pos === undefined) throw new Error(`No tile position for index ${index}`);
  const tileType = pos.type;

  const glyphDef = TILE_GLYPH[tileType] ?? null;

  // WEB.4.UI.7: picker mode overrides normal visual when active.
  // pickerMode is truthy ⟺ TilePicker is open. Normal hexVisual still
  // provides the base visual; picker classes are layered on top via className.
  const inPickerMode = pickerMode !== undefined && pickerMode !== null;
  const visual = hexVisual(index, hoveredTile, activeTile, ownerSeat);
  const hexPoints = flatTopHexPoints(x, y, HEX_R);
  const isStart = tileType === "Start";
  const ringPoints = flatTopHexPoints(x, y, HEX_R + 4);

  // Picker visual state
  const pickerPolygonClass = inPickerMode
    ? isPickerSelected
      ? styles.hexPickerSelected
      : isHighlighted
        ? styles.hexPickerValid
        : styles.hexPickerInvalid
    : visual.isActive
      ? styles.hexActive
      : styles.hexDefault;

  // Picker fill/stroke — override normal visual when picker active
  const pickerFill = inPickerMode
    ? isPickerSelected
      ? "rgba(255,180,32,0.55)"
      : isHighlighted
        ? "rgba(255,180,32,0.30)"
        : ownerSeat !== undefined
          ? visual.fill // keep seat tint for invalid owned tiles
          : "rgba(0,0,0,0.35)"
    : visual.fill;

  const pickerStroke = inPickerMode
    ? isPickerSelected
      ? "#FFD060"
      : isHighlighted
        ? "#FFB420"
        : "rgba(255,255,255,0.18)"
    : visual.stroke;

  // Destroy mode: hover on valid tile uses red
  const isHoveredValid = inPickerMode && isHighlighted && hoveredTile === index;
  const destroyHoverFill =
    isHoveredValid && pickerMode === "destroy" ? "rgba(239,83,80,0.35)" : pickerFill;
  const destroyHoverStroke = isHoveredValid && pickerMode === "destroy" ? "#EF5350" : pickerStroke;

  const effectiveFill = destroyHoverFill;
  const effectiveStroke = destroyHoverStroke;
  const effectiveStrokeWidth = inPickerMode
    ? isPickerSelected
      ? 3.5
      : isHighlighted
        ? 2.5
        : 1
    : visual.strokeWidth;

  // Picker group: invalid tiles get pointer-events:none
  const groupStyle: React.CSSProperties = inPickerMode
    ? isHighlighted || isPickerSelected
      ? { cursor: "pointer" }
      : { cursor: "not-allowed", pointerEvents: "none" }
    : { cursor: "pointer" };

  return (
    <g
      data-tile={index}
      data-tile-type={tileType}
      data-owned={ownerSeat !== undefined ? ownerSeat : undefined}
      data-picker-valid={inPickerMode ? (isHighlighted ? "true" : "false") : undefined}
      data-picker-selected={inPickerMode && isPickerSelected ? "true" : undefined}
      style={groupStyle}
      onMouseEnter={(e) => onHoverEnter(index, e)}
      onMouseLeave={() => onHoverLeave()}
      onClick={() => onClick(index)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClick(index);
      }}
      tabIndex={isHighlighted || !inPickerMode ? 0 : -1}
      // biome-ignore lint/a11y/useSemanticElements: SVG <g> cannot be replaced with <button>; keyboard handler is present above
      role="button"
      aria-label={`Tile ${index}: ${tileType}${isPickerSelected ? " (selected)" : isHighlighted ? " (valid target)" : ""}`}
      aria-disabled={inPickerMode && !isHighlighted && !isPickerSelected}
    >
      {/* Base hex */}
      <polygon
        points={hexPoints}
        fill={effectiveFill}
        stroke={effectiveStroke}
        strokeWidth={effectiveStrokeWidth}
        shapeRendering="crispEdges"
        className={pickerPolygonClass}
      />

      {/* Start accent ring */}
      {isStart && (
        <polygon
          points={ringPoints}
          fill="none"
          stroke="#FFB420"
          strokeWidth={2}
          opacity={0.9}
          shapeRendering="crispEdges"
        />
      )}

      {/* Special-function glyph (terrain tiles have no glyph) */}
      {glyphDef !== null && (
        <text
          x={x.toFixed(2)}
          y={(y + 2).toFixed(2)}
          textAnchor="middle"
          dominantBaseline="central"
          fontFamily="'VT323', monospace"
          fontSize={13}
          fill={glyphDef.fill}
          style={{ pointerEvents: "none", filter: "drop-shadow(0 0 2px rgba(0,0,0,0.85))" }}
        >
          {glyphDef.glyph}
        </text>
      )}

      {/* Facility icon — 20px sprite centered on tile, above glyph, below tokens.
          Real PNGs exist at /images/facilities/facility_{id}.png — no placeholder needed.
          data-testid="hex-facility-{tileIdx}" for tests. */}
      {facilityId !== undefined && (
        <image
          href={`/images/facilities/facility_${facilityId}.png`}
          x={(x - 10).toFixed(2)}
          y={(y - 22).toFixed(2)}
          width={20}
          height={20}
          style={{ pointerEvents: "none", imageRendering: "pixelated" }}
          className={styles.hexFacilityPop}
          data-testid={`hex-facility-${index}`}
          data-animation="hex-facility-pop"
        />
      )}

      {/* Tile-index label: 8px white α=0.80 with drop-shadow halo */}
      <text
        x={x.toFixed(2)}
        y={(y + HEX_R - 3).toFixed(2)}
        textAnchor="middle"
        dominantBaseline="auto"
        fontFamily="'VT323', monospace"
        fontSize={8}
        fill="rgba(255,255,255,0.80)"
        style={{
          pointerEvents: "none",
          filter: "drop-shadow(0 0 1.5px rgba(0,0,0,0.9)) drop-shadow(0 1px 1px rgba(0,0,0,0.8))",
        }}
      >
        {String(index).padStart(2, "0")}
      </text>

      {/* Active arrow above tile — only when NOT in picker mode */}
      {visual.isActive && !inPickerMode && (
        <text
          x={x.toFixed(2)}
          y={(y - HEX_R - 7).toFixed(2)}
          textAnchor="middle"
          dominantBaseline="central"
          fontFamily="'VT323', monospace"
          fontSize={14}
          fill="#FFB420"
          className={styles.arrowBounce}
          style={{
            pointerEvents: "none",
            filter: "drop-shadow(0 0 2px rgba(0,0,0,0.9))",
            transformOrigin: `${x.toFixed(1)}px ${(y - HEX_R - 7).toFixed(1)}px`,
          }}
        >
          ▼
        </text>
      )}

      {/* Picker: green ✓ in top-right corner of selected tile (WEB.4.UI.7) */}
      {isPickerSelected && (
        <text
          x={(x + 14).toFixed(2)}
          y={(y - 14).toFixed(2)}
          textAnchor="middle"
          dominantBaseline="central"
          fontFamily="'VT323', monospace"
          fontSize={16}
          fill="#44CC66"
          style={{
            pointerEvents: "none",
            filter: "drop-shadow(0 0 3px rgba(0,0,0,0.95))",
          }}
        >
          ✓
        </text>
      )}
    </g>
  );
}

// ---------------------------------------------------------------------------
// PlayerToken sub-component
// ---------------------------------------------------------------------------

interface TokenProps {
  seatIndex: number;
  cx: number;
  cy: number;
  transitionMs?: number;
}

function PlayerToken({ seatIndex, cx, cy, transitionMs = 900 }: TokenProps): React.ReactElement {
  const fill = SEAT_COLORS[seatIndex] ?? SEAT_COLORS[0];
  // R11-B: per-step CSS transition duration. HexBoard animates the token
  // through each intermediate tile by mutating cx/cy in steps; transitionMs
  // matches the per-step delay so each hop glides cleanly.
  return (
    <g
      transform={`translate(${cx.toFixed(2)} ${cy.toFixed(2)})`}
      style={{ transition: `transform ${transitionMs}ms cubic-bezier(0.4, 0, 0.2, 1)` }}
    >
      <circle
        cx={0}
        cy={0}
        r={TOKEN_R}
        fill={fill}
        stroke="rgba(255,255,255,0.92)"
        strokeWidth={2}
        shapeRendering="crispEdges"
      />
      <text
        x={0}
        y={1}
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily="'VT323', monospace"
        fontSize={8}
        fill="#1A0E00"
        fontWeight="bold"
        style={{ pointerEvents: "none" }}
      >
        P{seatIndex + 1}
      </text>
    </g>
  );
}

// ---------------------------------------------------------------------------
// HexBoard public API
// ---------------------------------------------------------------------------

export type PickerMode = "select" | "destroy" | "protect" | "heal" | "teleport";

export interface HexBoardProps {
  /** Applied to the outer container div. */
  className?: string;
  /** Fired when a tile polygon is clicked. */
  onTileClick?: (idx: number) => void;
  /** Fired when hover enters/leaves a tile; null = leave. */
  onTileHover?: (idx: number | null) => void;

  // ── WEB.4.UI.7 TilePicker props ──────────────────────────────────────────
  /** Tile indices that pass the current filter (render amber stroke + pulse). */
  highlightedTiles?: number[] | undefined;
  /** Currently-selected tile (brighter pulse + green ✓ corner). */
  selectedTile?: number | null | undefined;
  /** Called when a VALID (highlighted) tile is clicked in picker mode. */
  onTileSelect?: ((tileIdx: number) => void) | undefined;
  /** Picker mode — affects color accent on hover/confirm. */
  pickerMode?: PickerMode | undefined;
}

// ---------------------------------------------------------------------------
// HexBoard
// ---------------------------------------------------------------------------

export function HexBoard({
  className,
  onTileClick,
  onTileHover,
  highlightedTiles,
  selectedTile,
  onTileSelect,
  pickerMode,
}: HexBoardProps): React.ReactElement {
  const hoveredTile = useGameStore((s) => s.hoveredTile);
  const activeTile = useGameStore((s) => s.activeTile);
  const playerTiles = useGameStore((s) => s.playerTiles);
  const numPlayers = useGameStore((s) => s.numPlayers);
  const ownedBy = useGameStore((s) => s.ownedBy);
  const builtFacilities = useGameStore((s) => s.builtFacilities);
  const setHoveredTile = useGameStore((s) => s.setHoveredTile);
  const selectTile = useGameStore((s) => s.selectTile);

  // R11-B / R11.1: tile-by-tile movement animation. Store mutates playerTiles
  // synchronously on rollDice; we render through `renderedTiles` and step it
  // forward one tile at a time. R11.1 bumped budget from 900ms→1500ms for
  // slower, more readable pacing. SidescrollerScene gates its mount on the
  // store flag `boardAnimationActive` so the in-tile UI does not flash before
  // the walk completes.
  const STEP_BUDGET_MS = 2000;
  const MIN_STEP_MS = 240;
  const MAX_DICE_STEPS = 6; // d6 — distances > this assume teleport/escape, snap.
  const setBoardAnimationActive = useGameStore((s) => s.setBoardAnimationActive);
  const [renderedTiles, setRenderedTiles] = useState<number[]>(() => [...playerTiles]);
  const [stepMs, setStepMs] = useState(900);
  const animTimerRef = useRef<number | null>(null);
  const prevPlayerTilesRef = useRef<number[]>([...playerTiles]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: animation must observe playerTiles only.
  useEffect(() => {
    const prev = prevPlayerTilesRef.current;
    prevPlayerTilesRef.current = [...playerTiles];
    // Find the (single) seat that changed.
    let movedSeat = -1;
    for (let s = 0; s < playerTiles.length; s++) {
      if ((prev[s] ?? 0) !== (playerTiles[s] ?? 0)) {
        movedSeat = s;
        break;
      }
    }
    if (movedSeat < 0) return;
    const from = renderedTiles[movedSeat] ?? prev[movedSeat] ?? 0;
    const to = playerTiles[movedSeat] ?? 0;
    if (from === to) return;
    const forwardDist = (to - from + TILE_COUNT) % TILE_COUNT;
    // Snap on teleport / escape jail / non-forward jumps.
    if (forwardDist === 0 || forwardDist > MAX_DICE_STEPS) {
      setRenderedTiles((arr) => {
        const next = [...arr];
        next[movedSeat] = to;
        return next;
      });
      // R11.2: clear the board-anim flag immediately on snap so tile-stage
      // UI mounts without the visible UI being held off forever.
      setBoardAnimationActive(false);
      return;
    }
    const perStep = Math.max(MIN_STEP_MS, Math.round(STEP_BUDGET_MS / forwardDist));
    setStepMs(perStep);
    setBoardAnimationActive(true);
    let step = 0;
    if (animTimerRef.current !== null) window.clearInterval(animTimerRef.current);
    animTimerRef.current = window.setInterval(() => {
      step += 1;
      const nextTile = (from + step) % TILE_COUNT;
      setRenderedTiles((arr) => {
        const next = [...arr];
        next[movedSeat] = nextTile;
        return next;
      });
      if (step >= forwardDist && animTimerRef.current !== null) {
        window.clearInterval(animTimerRef.current);
        animTimerRef.current = null;
        // Hold the flag through one final transition frame so the in-tile
        // UI does not flash mid-fade.
        window.setTimeout(() => setBoardAnimationActive(false), perStep);
      }
    }, perStep);
    return () => {
      if (animTimerRef.current !== null) {
        window.clearInterval(animTimerRef.current);
        animTimerRef.current = null;
      }
      setBoardAnimationActive(false);
    };
  }, [playerTiles]);

  // BGM: cross-fade to era variant on era/round change (gameboard view only).
  // Variant: rounds 1-2 within era → A, round 3 → B. See bgmKeyForEraRound().
  // R13 Bug #4 fix: gate on boardAnimationActive so the era BGM does NOT fire
  // while the token is walking (endTurn advances round+era synchronously, so
  // without this guard the BGM would crossfade mid-walk then snap back to
  // terrain BGM once SidescrollerScene mounts, producing a 1s flicker).
  const era = useGameStore((s) => s.era);
  const round = useGameStore((s) => s.round);
  const audioMuted = useGameStore((s) => s.audioMuted);
  const boardAnimationActiveForBgm = useGameStore((s) => s.boardAnimationActive);
  // Gate-first (2026-06-13): no board BGM until the asset gate has dismissed —
  // a Howler stream during the gate competes with the critical-asset fetches
  // (the iPad stutter source). startIntroAfterGate flips this true.
  const assetGateDone = useGameStore((s) => s.assetGateDone);
  // R15 carry-forward (R13 Codex-A LOW): only crossfade on actual era/round
  // change, not when boardAnimationActive flips. Without this, finishing a
  // walk re-fires the effect even though the BGM key is identical, causing a
  // spurious 800ms crossfade flicker as terrain BGM takes over.
  const lastBgmKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (audioMuted) return;
    if (boardAnimationActiveForBgm) return;
    if (!assetGateDone) return; // gate-first: silence while assets load
    const key = bgmKeyForEraRound(era, round);
    if (lastBgmKeyRef.current === key) return;
    lastBgmKeyRef.current = key;
    crossfadeBGM(key, 800);
  }, [era, round, audioMuted, boardAnimationActiveForBgm, assetGateDone]);

  // ── BUG B (2026-06-13): bg ocean <video> iOS hardening ────────────────────
  // On iOS Safari a muted autoplay <video> can be paused by the OS / by Howler
  // grabbing the audio session, and stalls if the network hiccups. The visual
  // is a decorative loop; if it pauses or stalls we silently re-invoke play(),
  // and on a hard media error we reload the source. This does NOT change the
  // element's appearance — only keeps it playing. Handlers are also wired in
  // JSX (onPause/onStalled/onError) so React owns them; this effect kicks an
  // initial play() in case autoplay was deferred at mount.
  const bgVideoRef = useRef<HTMLVideoElement | null>(null);

  /**
   * Safely call play() on the bg video. Swallows both the rejected Promise
   * (autoplay blocked / no gesture) AND a synchronous throw (jsdom's
   * HTMLMediaElement.play is "Not implemented"), so this is inert in tests.
   */
  const tryPlayBgVideo = useCallback(() => {
    const v = bgVideoRef.current;
    if (v === null) return;
    try {
      const p = v.play();
      if (p !== undefined && typeof p.catch === "function") {
        p.catch(() => {
          /* autoplay rejected — a later pause/stalled event will retry */
        });
      }
    } catch {
      /* play() not implemented (jsdom) or element mid-teardown — ignore */
    }
  }, []);

  useEffect(() => {
    // Kick an initial play() in case autoplay was deferred at mount (iOS).
    tryPlayBgVideo();
  }, [tryPlayBgVideo]);

  /** Re-invoke play() when the bg video pauses or stalls (iOS autoplay guard). */
  const handleBgVideoResume = useCallback(() => {
    tryPlayBgVideo();
  }, [tryPlayBgVideo]);

  /** On a hard media error, reload the source and try to resume. */
  const handleBgVideoError = useCallback(() => {
    const v = bgVideoRef.current;
    if (v === null) return;
    try {
      v.load();
    } catch {
      /* load() can throw if the element is mid-teardown — ignore */
    }
    tryPlayBgVideo();
  }, [tryPlayBgVideo]);

  const handleHoverEnter = useCallback(
    (idx: number, e: React.MouseEvent) => {
      setHoveredTile(idx, { x: e.clientX, y: e.clientY });
      onTileHover?.(idx);
    },
    [setHoveredTile, onTileHover],
  );

  const handleHoverLeave = useCallback(() => {
    setHoveredTile(null);
    onTileHover?.(null);
  }, [setHoveredTile, onTileHover]);

  // WEB.4.UI.7: when picker mode is active, route valid tile clicks through
  // onTileSelect instead of the normal selectTile + onTileClick.
  // Invalid tiles already have pointer-events:none in their group style.
  const highlightedSet = highlightedTiles !== undefined ? new Set(highlightedTiles) : null;

  const handleClick = useCallback(
    (idx: number) => {
      if (highlightedSet !== null && onTileSelect !== undefined) {
        // Picker mode: only fire onTileSelect for valid (highlighted) tiles.
        if (highlightedSet.has(idx)) {
          onTileSelect(idx);
        }
        return; // do NOT call selectTile/onTileClick in picker mode
      }
      selectTile(idx);
      onTileClick?.(idx);
    },
    [selectTile, onTileClick, onTileSelect, highlightedSet],
  );

  // Build tile → [seats] map for stacked tokens.
  // R11-B: render from `renderedTiles` (animated position) instead of store
  // `playerTiles` (already at destination after rollDice).
  // Player-count support: render pawns ONLY for in-game seats (< numPlayers). Default 4
  // → all four rendered, byte-identical. Guards the historical ghost-player-at-origin bug:
  // not-in-game seats sit at INITIAL tile 0 and would otherwise stack a phantom pawn there.
  const tokensByTile = new Map<number, number[]>();
  renderedTiles.forEach((tileIdx, seat) => {
    if (seat >= numPlayers) return;
    const list = tokensByTile.get(tileIdx) ?? [];
    list.push(seat);
    tokensByTile.set(tileIdx, list);
  });

  return (
    <div className={`${styles.boardCanvas}${className ? ` ${className}` : ""}`}>
      {/* Layer 0: animated ocean video (upscaled web build). NO poster — user-locked: poster blocks bg vid. */}
      {/* BUG B: ref + onPause/onStalled/onError keep the loop playing on iOS,
          where Howler / the OS can pause a muted autoplay video. Visual unchanged. */}
      <video
        ref={bgVideoRef}
        className={styles.bgVideo}
        autoPlay
        loop
        muted
        playsInline
        onPause={handleBgVideoResume}
        onStalled={handleBgVideoResume}
        onError={handleBgVideoError}
      >
        <source src="/video/bg-loop-web-v2.mp4" type="video/mp4" />
      </video>

      {/* WEB.4.UI.18.PT (playtest #1): removed islandMap layer per user request.
          IslandMap rendered as letterboxed rectangle between bg vid and hex SVG
          and also leaked visually into TileStage. bg vid + hex grid is the new look. */}

      {/* Layer 2: SVG hex overlays + tokens */}
      <svg
        className={styles.boardSvg}
        viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`}
        role="img"
        aria-label="Watts and Wealth game board — 33 hex tiles"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Tiles */}
        <g className="tiles">
          {TILE_INDICES.map((i) => (
            <HexTile
              key={`tile-${i}`}
              index={i}
              hoveredTile={hoveredTile}
              activeTile={activeTile}
              ownerSeat={ownedBy[i]}
              onHoverEnter={handleHoverEnter}
              onHoverLeave={handleHoverLeave}
              onClick={handleClick}
              isHighlighted={highlightedSet?.has(i) ?? false}
              isPickerSelected={selectedTile === i}
              pickerMode={pickerMode}
              facilityId={builtFacilities[i]}
            />
          ))}
        </g>

        {/* Player tokens */}
        <g className="tokens">
          {Array.from(tokensByTile.entries()).map(([tileIdx, seats]) => {
            const { x, y } = tileCenter(tileIdx);
            return seats.map((seat, slot) => {
              const off =
                seats.length === 1 ? { dx: 0, dy: 0 } : (TOKEN_OFFSETS[slot] ?? { dx: 0, dy: 0 });
              return (
                <PlayerToken
                  key={`token-p${seat}`}
                  seatIndex={seat}
                  cx={x + off.dx}
                  cy={y + off.dy}
                  transitionMs={stepMs}
                />
              );
            });
          })}
        </g>
      </svg>
    </div>
  );
}
